import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, css, fonts, frames, glyphs, useMediaQuery } from '../../../Styles'
import { BasicInfo, Button, ButtonList, Events, Feed, FeedCurveViewer, Marking, Modal, PageHeader, SelectField, Timeline } from '../../../Components'
import { ButtonTypes, FieldStyles, ListStyle_Types } from '../../../Definitions'

import { TestData as animalData, Animals } from '../../../Assets/TestData/animalData'
import { useLocation } from 'react-router'
import { TestData as ComponentData } from '../../../Assets/TestData/componentsData';
import { TestData as FeedCurveData } from '../../../Assets/TestData/feedCurvesData';
import { indexOf } from 'lodash'

export default function Animal() {
  const {t} = useTranslation()
  let location = useLocation();
  const [selectedAnimal, setAnimal] = useState<number>(0)
  const [showAnimal, setShowAnimal] = useState<Animals|undefined>(undefined)
  const [showTimelineModal, setShowTimelineModal] = useState<boolean>(false)

  const [feedView, setFeedView] = useState<boolean>(true)
  const [markingView, setMarkingView] = useState<boolean>(false)
  const [eventsView, setEventsView] = useState<boolean>(false)


  // #region | Mediaqueries
  const vertical = useMediaQuery(`(max-width: 740px), (min-width: 1000px) and (max-width: 1060px)`);
  const hideBtns = useMediaQuery(`(max-width: 600px)`);
  const hideViewMenu = useMediaQuery(`(max-width: 500px), (min-width: 741px) and (max-width: 880px), (min-width: 1060px) and (max-width: 1195px)`);
  const heightMin = useMediaQuery(`(min-height: 910px)`);
  const heightMax = useMediaQuery(`(max-height: 785px)`);
  // #endregion

  useEffect(()=> {
    setShowAnimal(animalData.find((animal) => animal.animalId === selectedAnimal))
  },[selectedAnimal])

  // Get all animals for selectfield
  const animalOptions = useMemo(() => {
  const options = animalData.map(animal => ({
    label: animal.animalId.toString(),
    id: animal.animalId
  }));
  if (!showAnimal && location.state?.params?.selected) {
    setAnimal(location.state.params.selected);
  } else if (!showAnimal && options.length) {
    setAnimal(options[0].id);
  }
  return options;
}, [showAnimal, location.state]);

  useEffect(() => {
    if (location.state?.params?.selected && !showAnimal && animalData) {
      setAnimal(location.state.params.selected);
    }
  }, [location.state, selectedAnimal, showAnimal]);

  // #region | Viewer for Feed, Marking, Events...
  const viewer = 
    <div style={{height: "100%", width: "100%", display: "grid", gridTemplateColumns: "70px 1fr", gridTemplateRows: "50px calc(100% - 50px)"}}>
      {/* Buttons */}
      <div style={{ gridArea: "1/1/2/2", position: "relative", zIndex: "999", backgroundColor: "transparent", display: "flex", justifyContent: "start", padding: "0px 20px", gap: "5px"}}>
        <ButtonList 
          contents={[t("animalPages.animal.feed"), t("animalPages.animal.marking"), t("animalPages.animal.events")]} 
          onClick={(i => {
            if (i === 0) { setFeedView(true); setMarkingView(false); setEventsView(false)}
            else if (i === 1) { setMarkingView(true); setFeedView(false); setEventsView(false)}
            else if (i === 2) { setEventsView(true); setFeedView(false); setMarkingView(false)}
          })}
          overrides={{
            baseButton: { borderRadius: hideViewMenu ? "8px" : "8px 8px 0px 0px", padding: hideViewMenu ? "5px" : "0px 5px 2px 5px", backgroundColor: hideViewMenu ? colors.transparent : colors.greySecondary },
            hoverStyles: { backgroundColor: hideViewMenu ? colors.greyDarkSecondary : colors.greyPrimary }
          }}
          buttonType={ButtonTypes.tableButton}
          hasActiveLoad={true}
          startEnhancers={[glyphs.FeedPlan({}), glyphs.Animal({}), glyphs.Calendar({})]}
          listOverrides={{
            listGap: hideViewMenu ? "10px" : "5px"
          }}
          responsiveOverrides={{
            isResponsive: hideViewMenu ? true : false,
            popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "10px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d", right: "unset", top: "-10px", left: "55px"},
            buttonType: ButtonTypes.tableButton,
            openContent: "Menu",
            closeContent: "Menu",
            closeEnhancer: <div style={{marginBottom: "-4px"}}>{glyphs.Close({})}</div>,
            openEnhancer: <>{glyphs.Animal({})}</>
          }}
        />
      </div>
      {/* Content */}
        { feedView && <div style={{gridArea: "1/1/3/3", overflow: "hidden"}}><Feed selectedAnimal={selectedAnimal}/></div> }
        { markingView && <div style={{gridArea: "1/1/3/3", overflow: "hidden"}}><Marking selectedAnimal={selectedAnimal}/></div> }
        { eventsView && <div style={{gridArea: "1/1/3/3", overflow: "hidden"}}><Events selectedAnimal={selectedAnimal}/></div> }

    </div>
  // #endregion
  
  // #region | Timeline Modal
  const timelineModal = (
    <Modal modalHeader={'Timeline'}
      isModalOpen={!!showTimelineModal}
      onCloseBtn={() => setShowTimelineModal(false)}
      overrides={{
        isPrimaryComponent: true,
        root: { padding: "40px 5px 5px 5px" },
        background: colors.greyLightTertiary
      }}
    >
      <div style={{width: "100%"}}>
        <Timeline selectedAnimal={selectedAnimal}/>
      </div>
    </Modal>
  )
  // #endregion
  
  console.log('vertical:', vertical, 'heightMax:', heightMax);

  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <div style={{display: "flex", flexDirection: "row", alignItems: "start", justifyContent: "space-between"}}>
          <div>
            <PageHeader topHeader={t("globalMenu.animals")}/>
            <SelectField
              options={animalOptions}
              defaultValue={showAnimal?.animalId.toString()}
              overrides={{
                fieldStyle: FieldStyles.greyDarkPrimary,
                textStart: true                
              }}
              onChange={(e) => {
                setAnimal(parseInt(String(e[0].id)));
              }}
              isSearchable={true}
            />
          </div>
            {(heightMax || vertical) && <ButtonList 
              onClick={(index) => {
                if (index === 0) { setShowTimelineModal(true) }
              }}
              contents={["View Timeline"]} 
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: { ...( !hideBtns ? {...css.button_FitContent, minWidth: "none"} : {backgroundColor: colors.greyDarkSecondary})}
              }}
              listOverrides={{
                listStyle: !hideBtns ? ListStyle_Types.horizontal : ListStyle_Types.vertical,
              }}
              responsiveOverrides={{
                isResponsive: !hideBtns ? false : true,
                popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d" }
              }}
            /> }
        </div>
      </frames._page_inner_header>
      <frames._page_inner_content>

      <div style={{height: "100%", display: "grid", 
        gridTemplateColumns: vertical ? "1fr" : "360px 1fr", 
        gridTemplateRows: vertical ? "360px 1fr" : heightMin ? "1fr 360px" : heightMax ? "100%" : "300px calc(100% - 300px)", 
        columnGap: vertical ? "0px" : "20px", 
        rowGap: vertical ? "20px" : "0px", 
        overflowY: vertical ? "scroll" : "hidden", 
        scrollbarWidth: "thin", 
        scrollbarColor: colors.greyDarkPrimary+colors.transparent
      }}>
        {(heightMax || vertical)
          ? <>{timelineModal}</>
          : <div style={{gridArea: "1/1/2/3", display: "flex", alignItems: "center", justifyContent: "center"}}><Timeline selectedAnimal={selectedAnimal}/></div>
        }
        <div style={{gridArea: vertical ? "1/1/2/2" : heightMax ? "1/1/2/2" : "2/1/3/2"}}><BasicInfo selectedAnimal={selectedAnimal}/></div>
        <div style={{gridArea: vertical ? "2/1/3/2" : heightMax ? "1/2/2/3" : "2/2/3/3"}}>{viewer}</div>
      </div>        
      </frames._page_inner_content>
    </frames.Page_inner>
  );
}